import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { durations, timerLabel } from "../lib/durations";
import { unlockAudio } from "../lib/alarm";
import { amount, store } from "../lib/util";
import { RateBox } from "./Rating";
import { TimerCtl, TimerDock } from "./CookTimers";

export function useCookOpen() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const pop = () => setOpen(false);
    window.addEventListener("popstate", pop);
    return () => window.removeEventListener("popstate", pop);
  }, [open]);
  const show = () => { window.history.pushState({ ...(window.history.state || {}), cook: 1 }, ""); setOpen(true); };
  const hide = useCallback(() => { if (window.history.state?.cook) window.history.back(); else setOpen(false); }, []);
  return [open, show, hide];
}

function useScrollLock() {
  useLayoutEffect(() => {
    const y = window.scrollY, b = document.body.style, html = document.documentElement, back = document.activeElement, hs = window.history;
    const sr = hs.scrollRestoration;
    hs.scrollRestoration = "manual";
    Object.assign(b, { position: "fixed", top: `-${y}px`, left: "0", right: "0", width: "100%" });
    html.classList.add("cooking");
    return () => {
      Object.assign(b, { position: "", top: "", left: "", right: "", width: "" });
      html.classList.remove("cooking");
      const to = () => { html.style.scrollBehavior = "auto"; window.scrollTo(0, y); html.style.scrollBehavior = ""; };
      to();
      requestAnimationFrame(to);
      setTimeout(() => { to(); hs.scrollRestoration = sr; }, 120);
      back?.focus?.({ preventScroll: true });
    };
  }, []);
}

function useWakeLock() {
  const [state, setState] = useState("wakeLock" in navigator ? "pending" : "unsupported");
  useEffect(() => {
    if (!("wakeLock" in navigator)) return;
    let lock = null, alive = true;
    const req = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const old = lock;
        lock = await navigator.wakeLock.request("screen");
        old?.release?.().catch?.(() => {});
        if (!alive) { lock.release(); return; }
        setState("on");
        lock.addEventListener?.("release", () => alive && setState("off"));
      } catch (e) { if (alive) setState("off"); }
    };
    req();
    const vis = () => document.visibilityState === "visible" && req();
    document.addEventListener("visibilitychange", vis);
    return () => { alive = false; document.removeEventListener("visibilitychange", vis); lock?.release?.().catch?.(() => {}); };
  }, []);
  return state;
}

function WakeHint({ state }) {
  const [show] = useState(() => state === "unsupported" && !store.get("wakehint", false));
  const [vis, setVis] = useState(show);
  useEffect(() => { if (show) store.set("wakehint", true); }, [show]);
  if (!vis) return null;
  return (
    <p className="ckhint" role="status" data-testid="cook-wake-hint">
      Din skærm kan gå i dvale – tryk på skærmen en gang imellem
      <button type="button" aria-label="Skjul" onClick={() => setVis(false)} data-testid="cook-wake-hint-close">×</button>
    </p>
  );
}

function IngScreen({ r, n, checks, toggle }) {
  const base = r.base || 4;
  return (
    <>
      <p className="ckeye">Før du går i gang</p>
      <h2 data-testid="cook-screen-title">Ingredienser</h2>
      <p className="cksub" data-testid="cook-persons">Til {n} {n === 1 ? "person" : "personer"}. Tryk for at krydse af.</p>
      <ul className="ckings" data-testid="cook-ingredients">
        {r.ingredients.map((i, k) => (
          <li key={k}>
            <button type="button" className={checks[k] ? "done" : undefined} aria-pressed={!!checks[k]} onClick={() => toggle(k)} data-testid={`cook-ingredient-${k}`}>
              <span className="ckbox" aria-hidden="true">{checks[k] ? "✓" : ""}</span>
              <span className="amt" data-testid={`cook-ingredient-amount-${k}`}>{i.amount !== null ? amount(i.amount * n / base, i.unit) : ""}</span>
              <span className="nm">{i.name}</span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function StepScreen({ r, k, timers }) {
  const ds = durations(r.steps[k]);
  return (
    <>
      <p className="ckeye" data-testid="cook-step-counter">Trin {k + 1} af {r.steps.length}</p>
      <h2 data-testid="cook-screen-title">{r.step_names?.[k] || `Trin ${k + 1}`}</h2>
      <p className="cktext" data-testid="cook-step-text">{r.steps[k]}</p>
      {ds.length > 0 && (
        <div className="cktimers" data-testid="cook-step-timers">
          {ds.map((d, j) => {
            const t = timers.get(`${k + 1}-${d.secs}`);
            return t ? <TimerCtl key={d.secs} t={t} timers={timers} />
              : <button key={d.secs} type="button" className="btn cktstart" onClick={() => timers.start(k + 1, d.secs)} data-testid={`cook-timer-start-${k + 1}-${j}`}>Start {timerLabel(d.secs)}</button>;
          })}
        </div>
      )}
    </>
  );
}

function DoneScreen({ r, onClose, onRated }) {
  return (
    <div className="ckdone">
      <h2 data-testid="cook-screen-title">Velbekomme!</h2>
      <p className="cksub">Håber, at {r.title.toLowerCase()} smager. Giv gerne opskriften en bedømmelse.</p>
      <RateBox slug={r.slug} initial={{ count: r.rating_count, avg: r.rating_avg, shown: r.rating_avg != null }} onChange={onRated} tp="cook-" />
      <button type="button" className="btn ckbig" onClick={onClose} data-testid="cook-done-close">Luk</button>
    </div>
  );
}

function useSwipe(go) {
  const s = useRef(null);
  return {
    onTouchStart: e => { const t = e.touches[0]; s.current = e.touches.length === 1 ? { x: t.clientX, y: t.clientY } : null; },
    onTouchEnd: e => {
      if (!s.current) return;
      const t = e.changedTouches[0], dx = t.clientX - s.current.x, dy = t.clientY - s.current.y;
      s.current = null;
      if (Math.abs(dx) >= 60 && Math.abs(dx) > 1.5 * Math.abs(dy)) go(dx < 0 ? 1 : -1);
    },
  };
}

export function CookMode({ r, n, timers, onClose, onRated }) {
  const key = `cook:${r.slug}`;
  const last = r.steps.length + 1;
  const [st, setSt] = useState(() => { const v = store.get(key, null); return { i: Math.min(last, Math.max(0, v?.i || 0)), checks: v?.checks || {} }; });
  const box = useRef(null);
  const main = useRef(null);
  useScrollLock();
  const wake = useWakeLock();
  useEffect(() => { store.set(key, st); }, [key, st]);
  useEffect(() => { box.current?.focus({ preventScroll: true }); }, []);
  useEffect(() => { main.current?.scrollTo?.(0, 0); }, [st.i]);
  const go = useCallback(d => setSt(s => ({ ...s, i: Math.min(last, Math.max(0, s.i + d)) })), [last]);
  useEffect(() => {
    const kd = e => {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
  }, [go, onClose]);
  const swipe = useSwipe(go);
  const toggle = k => setSt(s => ({ ...s, checks: { ...s.checks, [k]: !s.checks[k] } }));
  const pos = st.i === 0 ? "Ingredienser" : st.i === last ? "Færdig" : `Trin ${st.i} af ${r.steps.length}`;
  return (
    <div className="cook noprint" role="dialog" aria-modal="true" aria-label={`Madlavning: ${r.title}`} tabIndex={-1} ref={box}
      onPointerDown={unlockAudio} data-testid="cook-mode" data-screen={st.i} data-wakelock={wake}>
      <header className="cktop">
        <p className="ckname"><small>Madlavning</small><b data-testid="cook-recipe-title">{r.title}</b></p>
        <button type="button" className="btn ghost" onClick={() => setSt({ i: 0, checks: {} })} data-testid="cook-restart">Start forfra</button>
        <button type="button" className="btn" onClick={onClose} data-testid="cook-close">Luk</button>
      </header>
      <div className="ckprog" role="progressbar" aria-label="Fremskridt" aria-valuemin={0} aria-valuemax={last} aria-valuenow={st.i} aria-valuetext={pos} data-testid="cook-progress">
        <span style={{ width: `${(st.i / last) * 100}%` }} data-testid="cook-progress-fill" />
      </div>
      <WakeHint state={wake} />
      <main className="ckmain" ref={main} {...swipe} data-testid="cook-screen">
        <div className="ckin" key={st.i}>
          {st.i === 0 ? <IngScreen r={r} n={n} checks={st.checks} toggle={toggle} />
            : st.i === last ? <DoneScreen r={r} onClose={onClose} onRated={onRated} />
            : <StepScreen r={r} k={st.i - 1} timers={timers} />}
        </div>
      </main>
      <TimerDock timers={timers} />
      <nav className="cknav" aria-label="Skift skærm">
        <button type="button" className="btn ghost" onClick={() => go(-1)} disabled={st.i === 0} data-testid="cook-prev">Forrige</button>
        <span className="ckpos" aria-live="polite" data-testid="cook-position">{pos}</span>
        <button type="button" className="btn" onClick={() => go(1)} disabled={st.i === last} data-testid="cook-next">Næste</button>
      </nav>
    </div>
  );
}
